import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Star, Quote } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ImagePicker from "@/components/admin/ImagePicker";

const EMPTY = { author: "", role: "", text: "", rating: 5, photo: "", featured: false, order: 0, published: true };

export default function ReviewsAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/reviews").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, rating: editing.rating ? Number(editing.rating) : null, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success("Avis enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${id}`);
    toast.success("Supprimé");
    load();
  };

  const togglePublish = async (item) => {
    await api.put(`/admin/reviews/${item.id}`, { ...item, published: !item.published });
    load();
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Avis</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="review-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {!items ? <p className="text-muted-foreground">Chargement…</p> : items.length === 0 ? (
        <div className="bg-white border border-line p-10 text-center" data-testid="reviews-empty">
          <Quote className="w-8 h-8 text-rouge mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aucun avis. Ajoutez les premiers témoignages de vos clients.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`review-row-${r.id}`}>
              {r.photo ? <img src={mediaUrl(r.photo)} alt="" className="w-10 h-10 rounded-full object-cover" /> : <Quote className="w-5 h-5 text-rouge shrink-0" />}
              <div className="flex-1 min-w-[220px]">
                <p className="font-display font-bold text-onyx">
                  {r.author} {r.featured && <Star className="w-3.5 h-3.5 inline fill-gold text-gold ml-1" />}
                  {!r.published && <span className="text-xs font-sans font-semibold text-muted-foreground ml-2">(masqué)</span>}
                </p>
                <p className="text-sm text-muted-foreground line-clamp-1">{r.text}</p>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!r.published} onCheckedChange={() => togglePublish(r)} data-testid={`review-published-${r.id}`} />
                <span className="text-xs text-muted-foreground">{r.published ? "Publié" : "Masqué"}</span>
              </div>
              <button onClick={() => setEditing({ ...EMPTY, ...r })} data-testid={`review-edit-${r.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Ajouter"} un avis</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Auteur *</Label><Input data-testid="review-author-input" className="mt-1.5" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} /></div>
                <div><Label>Qualité (ex : abonné OGC Nice)</Label><Input data-testid="review-role-input" className="mt-1.5" value={editing.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} /></div>
              </div>
              <div><Label>Témoignage *</Label><Textarea rows={4} data-testid="review-text-input" className="mt-1.5" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Note</Label>
                  <select data-testid="review-rating-select" className="mt-1.5 w-full border border-input bg-white px-3 py-2 text-sm"
                    value={editing.rating ?? ""} onChange={(e) => setEditing({ ...editing, rating: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">Aucune</option>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} / 5</option>)}
                  </select>
                </div>
                <div><Label>Ordre d'affichage</Label><Input type="number" data-testid="review-order-input" className="mt-1.5" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></div>
              </div>
              <ImagePicker label="Photo (optionnel)" value={editing.photo} onChange={(url) => setEditing({ ...editing, photo: url })} />
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex items-center gap-2">
                  <Switch data-testid="review-featured-switch" checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
                  <Label>Avis vedette</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch data-testid="review-published-switch" checked={!!editing.published} onCheckedChange={(v) => setEditing({ ...editing, published: v })} />
                  <Label>Publié</Label>
                </div>
              </div>
              <button onClick={save} disabled={saving || !editing.author || !editing.text} data-testid="review-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
